// frontend/src/pages/admin/Dashboard.js
import React, { useEffect, useState } from 'react';
import { Link } from '../../routing';
import { useTranslation } from 'react-i18next';
import { getIntlLocale } from '../../i18n';
import api from '../../utils/api';
import { TrendingUp, Package, AlertCircle, CheckCircle, ArrowRight, CheckCheck } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import { statusLabel } from './statusLabels';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats]             = useState(null);
  const [recentRentals, setRecentRentals] = useState([]);
  // Loaded on its own, like `attention` below and for the same reason. This used
  // to sit inside the Promise.all: a failing chart query blanked the stats cards
  // and the recent-rentals table with "we could not load the numbers", for data
  // that was then thrown away without being drawn at all.
  const [chartData, setChartData]     = useState([]);
  const [chartFailed, setChartFailed] = useState(false);
  const [loading, setLoading]         = useState(true);
  // Without this the dashboard renders zeros on an API failure, which reads as
  // "no revenue, no rentals" rather than "we could not load the numbers".
  const [loadError, setLoadError]     = useState(false);
  // What needs DOING, as opposed to the four numbers below it. Loaded
  // separately and allowed to fail on its own: a broken attention query must
  // not blank out the whole dashboard, and an empty one must never be mistaken
  // for "nothing needs doing".
  const [attention, setAttention]     = useState(null);
  const [attentionFailed, setAttentionFailed] = useState(false);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    Promise.all([
      api.get('/rentals/admin/stats'),
      api.get('/rentals?limit=10'),
    ]).then(([s, r]) => {
      setStats(s);
      // Limited server-side now. It used to fetch the whole first page (50) and
      // slice ten off it in the browser, which is forty rows of customer names,
      // emails and phone numbers sent to a screen that never showed them.
      setRecentRentals(Array.isArray(r) ? r : []);
    }).catch(() => setLoadError(true))
      .finally(() => setLoading(false));

    api.get('/rentals/admin/revenue-chart')
      .then((rows) => setChartData(Array.isArray(rows) ? rows : []))
      .catch(() => setChartFailed(true));

    api.get('/rentals/admin/attention')
      .then(setAttention)
      .catch(() => setAttentionFailed(true));
    return () => document.head.removeChild(link);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      {/* Main */}
      <div className="admin-main" style={{ padding: '40px' }}>
        <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, marginBottom: 8 }}>{t('admin.dashboard.title')}</h1>
        <p style={{ color: theme.grey400, marginBottom: 40 }}>{t('admin.dashboard.subtitle')}</p>

        {loadError && (
          <div style={{ background: theme.redWashPale, border: `1px solid ${theme.red}`, borderRadius: 4, padding: 16, marginBottom: 24, color: theme.ink, fontSize: 13 }}>
            {t('admin.loadError')}{' '}
            <button
              onClick={() => window.location.reload()}
              style={{ marginLeft: 8, padding: '6px 12px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, cursor: 'pointer', fontFamily: theme.font.body }}>
              {t('admin.retry')}
            </button>
          </div>
        )}

        {/* What needs doing.
            The four cards below this are a status report: active rentals,
            today's revenue, how many are late, how many items are free. Not one
            of them is a task. Everything an operator actually has to ACT on was
            scattered across alert emails and screens they had to think to open —
            a deposit sitting unrefunded past the 48 hours we promised, a paying
            customer whose cabinet code never issued, a machine that stopped
            answering. This is that list, and it leads. */}
        {attentionFailed && (
          <div style={{ background: theme.redWashPale, border: `1px solid ${theme.redBorder}`, borderRadius: 4, padding: 14, marginBottom: 24, fontSize: 13, color: theme.redSoft }}>
            {t('admin.dashboard.attentionFailed')}
          </div>
        )}

        {attention && (() => {
          const items = [
            { key: 'depositsToRefund', data: attention.deposits_to_refund, money: true, urgent: true },
            { key: 'strandedPickups',  data: attention.stranded_pickups,  urgent: true },
            { key: 'cabinetsOffline',  data: attention.cabinets_offline,  urgent: true },
            { key: 'openIssues',       data: attention.open_issues,       urgent: true },
            // Urgent because nothing else will move it: the reply window has
            // closed, so no cron, no deadline and no customer action will
            // resolve this rental. Only a person deciding will.
            { key: 'lostPending',      data: attention.lost_pending,      money: true, urgent: true },
            { key: 'overdue',          data: attention.overdue },
            { key: 'openDisputes',     data: attention.open_disputes },
            { key: 'noShows',          data: attention.no_shows },
          ].filter((i) => i.data && i.data.count > 0);

          if (!items.length) {
            return (
              <div style={{ background: theme.greenWashSoft, border: `1px solid ${theme.greenWash}`, borderRadius: 4, padding: '18px 22px', marginBottom: 32, display: 'flex', alignItems: 'center', gap: 12 }}>
                <CheckCheck size={20} color={theme.green} />
                <span style={{ color: theme.green, fontSize: 14 }}>{t('admin.dashboard.attentionClear')}</span>
              </div>
            );
          }

          return (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 24, color: theme.ink, margin: '0 0 14px' }}>
                {t('admin.dashboard.attentionTitle')}
              </h2>
              <div style={{ display: 'grid', gap: 10 }}>
                {items.map(({ key, data, money, urgent }) => (
                  <Link
                    key={key}
                    to={data.link}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      background: urgent ? theme.goldWash : theme.white,
                      border: `1px solid ${urgent ? theme.sandTan : theme.creamBone}`,
                      borderLeft: `3px solid ${urgent ? theme.amberDeep : theme.gold}`,
                      borderRadius: 4, padding: '14px 18px', textDecoration: 'none', color: theme.ink,
                    }}
                  >
                    <span style={{ fontFamily: theme.font.display, fontSize: 28, minWidth: 42 }}>
                      {data.count}
                    </span>
                    <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.5 }}>
                      {t(`admin.dashboard.attention.${key}`, { count: data.count })}
                      {/* The money is stated because "3 deposits outstanding" and
                          "€600 of other people's money we promised back within
                          48 hours" are not the same sentence. */}
                      {money && data.amount > 0 && (
                        <strong style={{ display: 'block', color: theme.amberDeep, marginTop: 2 }}>
                          €{Number(data.amount).toFixed(2)}
                        </strong>
                      )}
                    </span>
                    <ArrowRight size={16} color={theme.grey400} />
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Stats cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 20, marginBottom: 40 }}>
          {[
            { id: 'active', label: t('admin.dashboard.statActiveRentals'), value: stats?.active_rentals ?? '—', icon: <Package size={24} color={theme.gold} />, color: theme.white },
            { id: 'revenue', label: t('admin.dashboard.statTodayRevenue'), value: stats ? `€${stats.today_revenue.toFixed(2)}` : '—', icon: <TrendingUp size={24} color={theme.gold} />, color: theme.white },
            { id: 'overdue', label: t('admin.dashboard.statOverdueRentals'), value: stats?.late_rentals ?? '—', icon: <AlertCircle size={24} color={theme.red} />, color: stats?.late_rentals > 0 ? theme.redWashPale : theme.white },
            { id: 'available', label: t('admin.dashboard.statAvailableUnits'), value: stats?.available_products ?? '—', icon: <CheckCircle size={24} color={theme.green} />, color: theme.white },
          ].map(card => (
            <div key={card.id} style={{ background: card.color, border: `1px solid ${theme.creamBone}`, borderRadius: 4, padding: '28px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: theme.grey400, fontSize: 12, letterSpacing: '0.1em', marginBottom: 8 }}>{card.label}</div>
                  <div style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink }}>{card.value}</div>
                </div>
                {card.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Revenue, last 7 days.
            The endpoint behind this existed, with its day boundary and its
            discount arithmetic deliberately matched to the stat card above, and
            the dashboard fetched it on every load and threw the result away —
            there was no chart on this page at all. Drawn in plain CSS because
            the project has no charting dependency and seven bars do not justify
            adding one. */}
        {chartFailed ? (
          <div style={{ background: theme.redWashPale, border: `1px solid ${theme.redBorder}`, borderRadius: 4, padding: 14, marginBottom: 40, fontSize: 13, color: theme.redSoft }}>
            {t('admin.dashboard.chartFailed')}
          </div>
        ) : chartData.length > 0 && (
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, padding: '24px 28px 20px', marginBottom: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 22, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 24, margin: 0, color: theme.ink }}>
                {t('admin.dashboard.chartTitle')}
              </h2>
              <span style={{ fontSize: 13, color: theme.grey400 }}>
                {t('admin.dashboard.chartTotal', {
                  total: chartData.reduce((sum, d) => sum + Number(d.revenue || 0), 0).toFixed(2),
                })}
              </span>
            </div>
            {(() => {
              const values = chartData.map(d => Number(d.revenue || 0));
              const peak = Math.max(...values);
              // A flat week of zeros must read as "nothing came in", not as an
              // axis that failed to draw.
              if (peak === 0) {
                return (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: theme.grey400, fontSize: 13 }}>
                    {t('admin.dashboard.chartEmpty')}
                  </div>
                );
              }
              return (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 160 }}>
                  {chartData.map((d, i) => {
                    const value = values[i];
                    // Split on the parts rather than letting Date parse the
                    // string: 'YYYY-MM-DD' is read as UTC midnight, which lands
                    // on the previous calendar day for any operator west of
                    // Greenwich. These are local dates, so build a local date.
                    const [y, m, day] = String(d.day || '').split('-').map(Number);
                    const date = y ? new Date(y, m - 1, day) : null;
                    const isToday = i === chartData.length - 1;
                    return (
                      <div key={d.day || i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: 6 }}>
                        <span style={{ fontSize: 11, color: value > 0 ? theme.ink : theme.grey400, fontFamily: theme.font.display }}>
                          €{value.toFixed(0)}
                        </span>
                        <div
                          title={t('admin.dashboard.chartBarTitle', { count: Number(d.rentals || 0) })}
                          style={{
                            width: '100%',
                            // A day with money must never render as a bar of
                            // zero height, or "€3" looks identical to "€0".
                            height: `${Math.max(value > 0 ? 3 : 1, (value / peak) * 100)}%`,
                            background: isToday ? theme.gold : theme.sandTan,
                            borderRadius: '2px 2px 0 0',
                            minHeight: 2,
                          }}
                        />
                        <span style={{ fontSize: 11, color: isToday ? theme.ink : theme.grey400, letterSpacing: '0.04em' }}>
                          {date ? date.toLocaleDateString(getIntlLocale(), { weekday: 'short' }) : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* Quick actions */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 40, flexWrap: 'wrap' }}>
          <Link to="/admin/products" style={actionBtn}>{t('admin.dashboard.addProduct')}</Link>
          <Link to="/admin/rentals" style={actionBtn}>{t('admin.dashboard.manageRentals')}</Link>
          <Link to="/admin/rentals?status=late" style={{ ...actionBtn, background: theme.white, color: theme.red, border: `1px solid ${theme.red}` }}>{t('admin.dashboard.viewLateRentals')}</Link>
        </div>

        {/* Recent rentals */}
        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${theme.creamBone}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontFamily: theme.font.display, fontSize: 24, margin: 0, color: theme.ink }}>{t('admin.dashboard.recentRentals')}</h2>
            <Link to="/admin/rentals" style={{ color: theme.gold, fontSize: 13, textDecoration: 'none' }}>{t('admin.dashboard.viewAll')}</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: theme.cream }}>
                  {['colCustomer', 'colProduct', 'colDays', 'colStatus', 'colRevenue', 'colDue'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{t(`admin.dashboard.${h}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: theme.gold }}>{t('admin.dashboard.loading')}</td></tr>
                ) : recentRentals.map(r => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 500 }}>{r.full_name || t('admin.dashboard.customerFallback')}</div>
                      <div style={{ color: theme.grey400, fontSize: 12 }}>{r.email}</div>
                    </td>
                    <td style={{ padding: '14px 16px', color: theme.grey600 }}>{r.product_name}</td>
                    <td style={{ padding: '14px 16px', color: theme.grey600 }}>{t('admin.dashboard.daysShort', { count: r.rental_days })}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <StatusBadge status={r.status} />
                    </td>
                    <td style={{ padding: '14px 16px', fontFamily: theme.font.display, fontSize: 18 }}>€{parseFloat(r.total_rental_price).toFixed(0)}</td>
                    <td style={{ padding: '14px 16px', color: theme.grey400, fontSize: 12 }}>
                      {r.due_at ? new Date(r.due_at).toLocaleDateString(getIntlLocale()) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  pending_payment: theme.state.pending,
  paid: theme.state.success,
  picked_up: theme.state.info,
  returned: theme.state.neutral,
  late: theme.state.danger,
  cancelled: theme.state.muted,
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const cfg = STATUS_COLORS[status] || { bg: theme.grey100, color: theme.grey600 };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 100, fontSize: 11, letterSpacing: '0.05em', fontWeight: 500 }}>
      {statusLabel(t, status)}
    </span>
  );
}

const actionBtn = {
  display: 'inline-block',
  background: theme.ink, color: theme.white,
  padding: '12px 28px', borderRadius: 2,
  textDecoration: 'none', fontSize: 13,
  letterSpacing: '0.1em'
};
